import { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, CircularProgress, Paper, Tabs, Tab,
  Select, MenuItem, FormControl, InputLabel, Alert, Chip,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Download as DownloadIcon, Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { ReportesService } from '../services/reportesService';
import { PlanillonService } from '../../planillon/services/planillonService';
import type { PlanillaCabecera } from '../../planillon/types/planillon';
import type { LibroRemuneracionesData, TRegistroData } from '../types/reportes';

const fmt = (n: number) => `S/ ${n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function ReportesPage() {
  const [tab, setTab] = useState(0);
  const [planillas, setPlanillas] = useState<PlanillaCabecera[]>([]);
  const [selectedPlanilla, setSelectedPlanilla] = useState<number>(0);
  const [libroData, setLibroData] = useState<LibroRemuneracionesData | null>(null);
  const [tRegistroData, setTRegistroData] = useState<TRegistroData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPlanillas();
  }, []);

  const loadPlanillas = async () => {
    try {
      const data = await PlanillonService.getAll();
      const valid = data.filter(p => p.estado !== 'BORRADOR');
      setPlanillas(valid);
      if (valid.length > 0) setSelectedPlanilla(valid[0].idPlanilla);
    } catch { /* handled */ }
  };

  const handleLoadLibro = async () => {
    if (!selectedPlanilla) return;
    setLoading(true);
    try {
      const data = await ReportesService.getLibroRemuneraciones(selectedPlanilla);
      setLibroData(data);
    } catch { /* handled */ }
    finally { setLoading(false); }
  };

  const handleExportLibroCsv = async () => {
    if (!selectedPlanilla) return;
    try {
      await ReportesService.exportLibroRemuneracionesCsv(selectedPlanilla);
    } catch { /* handled */ }
  };

  const handleLoadTRegistro = async () => {
    setLoading(true);
    try {
      const data = await ReportesService.getTRegistro();
      setTRegistroData(data);
    } catch { /* handled */ }
    finally { setLoading(false); }
  };

  const handleExportTRegistroCsv = async () => {
    try {
      await ReportesService.exportTRegistroCsv();
    } catch { /* handled */ }
  };

  const libroColumns: GridColDef[] = [
    { field: 'numero', headerName: 'N°', width: 50 },
    { field: 'tipoDocumento', headerName: 'Tipo', width: 60 },
    { field: 'numeroDocumento', headerName: 'Documento', width: 110 },
    { field: 'apellidosNombres', headerName: 'Apellidos y Nombres', flex: 1, minWidth: 200 },
    { field: 'fechaIngreso', headerName: 'F. Ingreso', width: 100, valueFormatter: (v) => v ? new Date(v).toLocaleDateString('es-PE') : '' },
    { field: 'diasLaborados', headerName: 'Días', width: 60, type: 'number' },
    { field: 'totalRemuneraciones', headerName: 'Rem.', width: 110, valueFormatter: (v) => fmt(v) },
    { field: 'totalDescuentos', headerName: 'Desc.', width: 110, valueFormatter: (v) => fmt(v) },
    { field: 'netoPagar', headerName: 'Neto', width: 110, valueFormatter: (v) => fmt(v) },
    { field: 'regimenPensionario', headerName: 'Reg. Pen.', width: 100 },
    { field: 'aportePensionario', headerName: 'Ap. Pen.', width: 100, valueFormatter: (v) => fmt(v) },
    { field: 'esSalud', headerName: 'EsSalud', width: 100, valueFormatter: (v) => fmt(v) },
    { field: 'renta5ta', headerName: 'Rta 5ta', width: 100, valueFormatter: (v) => fmt(v) },
  ];

  const tRegistroColumns: GridColDef[] = [
    { field: 'tipoDocumento', headerName: 'Tipo', width: 60 },
    { field: 'numeroDocumento', headerName: 'Documento', width: 110 },
    { field: 'apellidosNombres', headerName: 'Apellidos y Nombres', flex: 1, minWidth: 200 },
    { field: 'fechaIngreso', headerName: 'F. Ingreso', width: 100, valueFormatter: (v) => v ? new Date(v).toLocaleDateString('es-PE') : '' },
    { field: 'regimenLaboral', headerName: 'Reg. Laboral', width: 130 },
    { field: 'categoriaTrabajador', headerName: 'Categoría', width: 110 },
    { field: 'situacionTrabajador', headerName: 'Situación', width: 100 },
    { field: 'regimenPensionario', headerName: 'Reg. Pensionario', width: 130 },
    { field: 'cussp', headerName: 'CUSSP', width: 130 },
    { field: 'modalidadContrato', headerName: 'Modalidad', width: 120 },
    { field: 'esRegimenMicroempresa', headerName: 'Micro?', width: 70, type: 'boolean' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Reportes Avanzados</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Libro de Remuneraciones" />
        <Tab label="T-Registro" />
      </Tabs>

      {/* ── Libro de Remuneraciones ── */}
      {tab === 0 && (
        <>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 350 }}>
              <InputLabel>Seleccionar Planilla</InputLabel>
              <Select value={selectedPlanilla || ''} label="Seleccionar Planilla"
                onChange={(e) => { setSelectedPlanilla(Number(e.target.value)); setLibroData(null); }}>
                {planillas.map(p => (
                  <MenuItem key={p.idPlanilla} value={p.idPlanilla}>
                    {p.descripcion} - {p.estado}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              onClick={handleLoadLibro} disabled={loading || !selectedPlanilla}>
              Generar
            </Button>
            {libroData && (
              <Button variant="contained" color="success" startIcon={<DownloadIcon />}
                onClick={handleExportLibroCsv}>
                Exportar CSV
              </Button>
            )}
          </Stack>

          {libroData && (
            <>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={4}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Periodo</Typography>
                    <Typography variant="h6">{libroData.periodo}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Trabajadores</Typography>
                    <Typography variant="h6">{libroData.totalTrabajadores}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Total Remuneraciones</Typography>
                    <Typography variant="h6" color="success.main">{fmt(libroData.totalRemuneraciones)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Total Neto</Typography>
                    <Typography variant="h6" color="primary">{fmt(libroData.totalNeto)}</Typography>
                  </Box>
                </Stack>
              </Paper>
              <DataGrid
                rows={libroData.registros}
                columns={libroColumns}
                getRowId={(row) => row.numero}
                autoHeight
                density="compact"
                disableRowSelectionOnClick
                pageSizeOptions={[25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              />
            </>
          )}
        </>
      )}

      {/* ── T-Registro ── */}
      {tab === 1 && (
        <>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              onClick={handleLoadTRegistro} disabled={loading}>
              Generar T-Registro
            </Button>
            {tRegistroData && (
              <Button variant="contained" color="success" startIcon={<DownloadIcon />}
                onClick={handleExportTRegistroCsv}>
                Exportar CSV
              </Button>
            )}
          </Stack>

          {tRegistroData && (
            <>
              {tRegistroData.errores.length > 0 && (
                <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Advertencias ({tRegistroData.errores.length})</Typography>
                  {tRegistroData.errores.map((err, i) => (
                    <Typography key={i} variant="body2">- {err}</Typography>
                  ))}
                </Alert>
              )}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={4}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Total Trabajadores</Typography>
                    <Typography variant="h6">{tRegistroData.totalTrabajadores}</Typography>
                  </Box>
                  <Chip label="Registro de alta de trabajadores para SUNAT" size="small" color="info" />
                </Stack>
              </Paper>
              <DataGrid
                rows={tRegistroData.trabajadores.map((t, i) => ({ ...t, _id: i }))}
                columns={tRegistroColumns}
                getRowId={(row) => row._id}
                autoHeight
                density="compact"
                disableRowSelectionOnClick
                pageSizeOptions={[25, 50]}
                initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
}
